interface CacheItem<T> {
  data: T;
  timestamp: number;
  ttl: number;
}

class FirestoreCache {
  private cache = new Map<string, CacheItem<any>>();
  private defaultTTL = 5 * 60 * 1000; // 5 minutes
  private storageKey = 'app_cache_v1';
  private maxEntries = 200;

  constructor() {
    // Load persisted cache from localStorage on creation so cached entries
    // survive a full page reload and reduce Firestore reads.
    try {
      this.loadFromStorage();
      console.log(`🔁 Cache loaded from storage (${this.cache.size} entries)`);
    } catch (e) {
      // ignore
    }
  }

  async get<T>(key: string, fetchFn: () => Promise<T>, ttl?: number): Promise<T> {
    const cached = this.cache.get(key);
    
    const now = Date.now();

    if (cached && now - cached.timestamp < (ttl || cached.ttl || this.defaultTTL)) {
      // Refresh last-access by re-inserting to Map to keep LRU-ish ordering
      this.cache.delete(key);
      this.cache.set(key, cached);
      console.log(`🔥 Cache HIT: ${key}`);
      return cached.data;
    }

    if (cached) {
      console.log(`⚠️ Cache STALE or EXPIRED: ${key}`);
    } else {
      console.log(`🔄 Cache MISS: ${key}`);
    }

    const data = await fetchFn();
    this.set(key, data, ttl);
    return data;
  }

  invalidate(key: string): void {
    this.cache.delete(key);
    this.persistToStorage();
  }

  invalidatePattern(pattern: RegExp): void {
    Array.from(this.cache.keys()).forEach(key => {
      if (pattern.test(key)) {
        this.cache.delete(key);
      }
    });
    this.persistToStorage();
  }

  // For React hooks integration
  getCacheState(key: string): { hasCache: boolean; isFresh: boolean; age?: number; ttl?: number } {
    const cached = this.cache.get(key);
    if (!cached) return { hasCache: false, isFresh: false };

    const age = Date.now() - cached.timestamp;
    return {
      hasCache: true,
      isFresh: age < (cached.ttl || this.defaultTTL),
      age,
      ttl: cached.ttl
    };
  }

  // Persist cache map to localStorage where available
  private persistToStorage() {
    try {
      if (typeof window === 'undefined' || !window.localStorage) return;
      const obj: Record<string, { data: any; timestamp: number; ttl: number }> = {};
      this.cache.forEach((v, k) => {
        try { obj[k] = { data: v.data, timestamp: v.timestamp, ttl: v.ttl }; } catch (e) { /* ignore */ }
      });
      localStorage.setItem(this.storageKey, JSON.stringify(obj));
    } catch (e) {
      // ignore storage errors
    }
  }

  // Load persisted cache from localStorage
  private loadFromStorage() {
    try {
      if (typeof window === 'undefined' || !window.localStorage) return;
      const raw = localStorage.getItem(this.storageKey);
      if (!raw) return;
      const obj = JSON.parse(raw) as Record<string, { data: any; timestamp: number; ttl: number }>;
      Object.keys(obj).forEach(k => {
        const v = obj[k];
        // Ignore expired entries
        if (Date.now() - v.timestamp < (v.ttl || this.defaultTTL)) {
          this.cache.set(k, { data: v.data, timestamp: v.timestamp, ttl: v.ttl });
        }
      });
    } catch (e) {
      // ignore parse errors
    }
  }

  // Internal helper to set and persist with LRU eviction
  private set(key: string, data: any, ttl?: number) {
    if (this.cache.size >= this.maxEntries) {
      // evict oldest by timestamp
      let oldestKey: string | null = null;
      let oldestTs = Infinity;
      this.cache.forEach((v, k) => {
        if (v.timestamp < oldestTs) {
          oldestTs = v.timestamp;
          oldestKey = k;
        }
      });
      if (oldestKey) this.cache.delete(oldestKey);
    }

    this.cache.set(key, { data, timestamp: Date.now(), ttl: ttl || this.defaultTTL });
    this.persistToStorage();
  }

  // Clear entire cache
  clearAll() {
    this.cache.clear();
    try { if (typeof window !== 'undefined' && window.localStorage) localStorage.removeItem(this.storageKey); } catch (e) {}
  }
}

// Singleton instance
export const cache = new FirestoreCache();